import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import colors, { SBI_DARK, SBI_LIGHT } from "assets/theme/base/colors";
import { useMaterialUIController } from "context";
import { FRESENIUS } from "contextBranding";

function UpcomingEventsCard({ events, isFreseniusBranding = false }) {
  const [controller] = useMaterialUIController();
  const isLight = controller?.sbiDashboardTheme === "light";
  const sb = colors.backgroundSbi;
  const themeColors = isLight ? SBI_LIGHT : SBI_DARK;
  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const cardBg = isFreseniusBranding
    ? (isLight ? freseniusTheme.bgHeader : `${FRESENIUS.blueDark}ee`)
    : (isLight ? themeColors.bgHeader : `${sb.default}99`);
  const cardBorder = isFreseniusBranding
    ? (isLight ? FRESENIUS.blue : freseniusTheme.border)
    : (isLight ? themeColors.border : `${sb.default}80`);

  const getEventTypeColor = (type) => {
    if (isFreseniusBranding && type === "Masterclass") return FRESENIUS.blueLight;
    if (isFreseniusBranding && type === "Bootcamp") return FRESENIUS.blue;
    switch (type) {
      case "Masterclass":
        return "#4ECDC4";
      case "Podcast":
        return "#FFD700";
      case "Bootcamp":
        return "#6BCF7F";
      default:
        return "#95A5A6";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
  };

  const handleOpen = (eventId) => {
    console.log("Open event:", eventId);
  };

  const handleAddToCalendar = (eventId) => {
    console.log("Add to calendar:", eventId);
  };

  if (!events || events.length === 0) {
    return (
      <Card
        sx={(theme) => ({
          height: "100%",
          borderRadius: theme.borders.borderRadius.lg,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          backgroundColor: cardBg,
          border: `1px solid ${cardBorder}`,
          backdropFilter: "blur(12px)",
        })}
      >
        <MDBox p={3} textAlign="center">
          <Icon
            sx={{
              fontSize: "3rem",
              color: isFreseniusBranding ? "white" : "text",
              mb: 2,
            }}
          >
            event_busy
          </Icon>
          <MDTypography
            variant="h6"
            color={isFreseniusBranding ? "white" : "text"}
          >
            No events scheduled
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card
      sx={(theme) => ({
        borderRadius: theme.borders.borderRadius.lg,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        backdropFilter: "blur(12px)",
      })}
    >
      <MDBox p={2}>
        <MDBox display="flex" alignItems="center" mb={2}>
          <Icon
            sx={{
              fontSize: "1.5rem",
              mr: 1,
              color: isFreseniusBranding ? "white" : "text",
            }}
          >
            event
          </Icon>
          <MDTypography
            variant="h5"
            fontWeight="bold"
            color={isFreseniusBranding ? "white" : "dark"}
          >
            Upcoming Events
          </MDTypography>
        </MDBox>

        {/* Horizontal scrollable container */}
        <MDBox
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            overflowY: "hidden",
            pb: 1,
            "&::-webkit-scrollbar": {
              height: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          {events.map((event) => (
            <MDBox
              key={event.id}
              sx={{
                minWidth: "280px",
                maxWidth: "280px",
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid rgba(0,0,0,0.1)",
                backgroundColor: "rgba(0,0,0,0.02)",
                transition: "all 0.2s ease",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
            >
              <MDBox
                display="flex"
                flexDirection="column"
                gap={1}
                height="100%"
              >
                {/* Top row: Event type on left, Date on right */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0.5}
                >
                  <Chip
                    label={event.type}
                    size="small"
                    sx={{
                      backgroundColor: getEventTypeColor(event.type),
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      height: "24px",
                    }}
                  />
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color={isFreseniusBranding ? "white" : "dark"}
                    >
                      {formatDate(event.date)}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color={isFreseniusBranding ? "white" : "text"}
                      fontSize="0.65rem"
                    >
                      {event.time}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                {/* Event content */}
                <MDBox flex={1} display="flex" flexDirection="column">
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    color={isFreseniusBranding ? "white" : "dark"}
                    mb={0.5}
                  >
                    {event.title}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color={isFreseniusBranding ? "white" : "text"}
                    mb={1}
                  >
                    {event.instructor} • {event.duration}
                  </MDTypography>
                  <MDBox display="flex" justifyContent="space-between" gap={1} flexWrap="wrap" mt="auto">
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={() => handleOpen(event.id)}
                      sx={(theme) => ({
                        ...(isFreseniusBranding && {
                          borderColor: FRESENIUS.white,
                          color: FRESENIUS.white,
                          "&:hover": {
                            borderColor: FRESENIUS.blueLight,
                            backgroundColor: `${FRESENIUS.blueLight}26`,
                            color: FRESENIUS.white,
                          },
                        }),
                      })}
                    >
                      Open
                    </MDButton>
                    <MDButton
                      size="small"
                      variant="text"
                      color="info"
                      onClick={() => handleAddToCalendar(event.id)}
                      sx={(theme) => ({
                        ...(isFreseniusBranding && {
                          color: FRESENIUS.white,
                          "&:hover": {
                            color: FRESENIUS.white,
                            backgroundColor: `${FRESENIUS.blueLight}26`,
                          },
                        }),
                      })}
                    >
                      Add to Calendar
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default UpcomingEventsCard;
